import React, { useEffect, useMemo, useRef, useState } from "react";
import { fmtIST } from "../lib/time";
import { api, formatApiErrorDetail } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { ScanLine, CheckCircle2, XCircle, ArrowLeft, PackageCheck, ListChecks, AlertTriangle, Wand2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function OutwardScan() {
  const [input, setInput] = useState("");
  const [lookup, setLookup] = useState(null);
  const [issueQty, setIssueQty] = useState("");
  const [order, setOrder] = useState("");
  const [mrId, setMrId] = useState("");
  const [adHocReason, setAdHocReason] = useState("");
  const [fifoOverride, setFifoOverride] = useState("");
  const [mrs, setMrs] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [recent, setRecent] = useState([]);
  const [issueDetail, setIssueDetail] = useState(null);     // Wave-G #5
  // Wave-C: FIFO suggestion (auto-pick) state
  const [fifoSuggest, setFifoSuggest] = useState(null);   // {lines: [...]} from /api/outward/fifo-suggest
  const [fifoLoading, setFifoLoading] = useState(false);
  const [fifoCurrentIdx, setFifoCurrentIdx] = useState({ line: 0, box: 0 });
  const inputRef = useRef(null);
  const nav = useNavigate();
  const { user, logout } = useAuth();

  const isPHorSA = user?.role?.is_super_admin || user?.role?.name === "Plant Head/Admin";
  const step1Complete = !!mrId || (!!adHocReason && isPHorSA);
  const refocus = () => {
    if (!step1Complete) return;
    // Do not steal focus if user is actively typing in another field
    const ae = document.activeElement;
    if (ae && ae !== inputRef.current && ae !== document.body) {
      const tag = (ae.tagName || "").toUpperCase();
      if (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA" || tag === "BUTTON") return;
    }
    inputRef.current?.focus();
  };
  useEffect(() => { refocus(); const t = setInterval(refocus, 800); return () => clearInterval(t); }, [step1Complete]); // eslint-disable-line

  useEffect(() => {
    api.get("/issues").then((r) => setRecent(r.data.slice(0, 8))).catch(() => {});
    api.get("/material-requests").then((r) => {
      setMrs((r.data || []).filter((m) => m.status === "approved" || m.status === "partially_fulfilled"));
    }).catch(() => {});
  }, [feedback]);

  const selectedMR = mrs.find((m) => m.id === mrId);
  // Derive default order from MR
  useEffect(() => {
    if (selectedMR && !order) setOrder(selectedMR.production_order || selectedMR.number);
  }, [mrId]); // eslint-disable-line

  const resetStep1 = () => {
    setMrId(""); setAdHocReason(""); setLookup(null);
    setInput(""); setIssueQty(""); setOrder(""); setFifoOverride(""); setFeedback(null);
    setFifoSuggest(null); setFifoCurrentIdx({ line: 0, box: 0 });
  };

  // Wave-C: Auto-pick FIFO. Call backend /api/outward/fifo-suggest for the
  // currently selected MR (per-material remaining). Show a confirmable list
  // of boxes; pre-fill the scan input with the next pending one so the user
  // can simply press Enter (or rescan) to consume.
  const fetchFIFOSuggest = async () => {
    if (!mrId) { setFeedback({ type: "warn", msg: "Select an approved MR first to auto-pick." }); return; }
    setFifoLoading(true);
    try {
      const { data } = await api.post("/outward/fifo-suggest", { material_request_id: mrId });
      setFifoSuggest(data);
      // Reset cursor to first non-empty line
      const firstLine = (data.lines || []).findIndex((l) => (l.boxes || []).length > 0);
      setFifoCurrentIdx({ line: Math.max(0, firstLine), box: 0 });
      setFeedback(null);
    } catch (e) {
      setFeedback({ type: "error", msg: formatApiErrorDetail(e.response?.data?.detail) || "FIFO suggest failed" });
    } finally {
      setFifoLoading(false);
    }
  };

  // Apply the currently-pointed FIFO pick: pre-fill the barcode + issue qty
  // so the user can simply hit Confirm. They CAN edit (override). If the
  // pick is for a newer lot, the existing FIFO-warning + override-approval
  // flow kicks in unchanged.
  const applyFIFOPick = async (lineIdx, boxIdx) => {
    if (!fifoSuggest) return;
    const line = fifoSuggest.lines[lineIdx];
    if (!line) return;
    const box = (line.boxes || [])[boxIdx];
    if (!box) return;
    setInput(box.barcode);
    setIssueQty(box.take);
    setFifoCurrentIdx({ line: lineIdx, box: boxIdx });
    // Run lookup so the confirm tile populates with the actual stock state
    await doLookup(box.barcode);
    // Override the suggested qty with the FIFO-pick qty (lookup defaults to box-remaining)
    setIssueQty(box.take);
  };

  const doLookup = async (value) => {
    if (!value) return;
    if (!mrId && !adHocReason) {
      setFeedback({ type: "error", msg: "Step 1 incomplete — pick an approved MR or enter an ad-hoc reason (PH/SA only)" });
      return;
    }
    try {
      const { data } = await api.post("/outward/lookup", { barcode: value.trim() });
      setLookup(data);
      // Default issue qty: prefer MR remaining for this material, capped by box qty.
      // - MR for 5 + box of 10 → suggest 5  (was suggesting 10, forcing manual edit)
      // - MR for 15 + box of 10 → suggest 10 (then user scans next box, suggest 5)
      let suggested = data.qty_remaining;
      let info = null;
      if (selectedMR) {
        const fulfilled = selectedMR.fulfilled_qty || {};
        const mrItem = (selectedMR.items || []).find((x) => x.material_id === data.material_id);
        if (mrItem) {
          const remaining = Math.max(0, (mrItem.qty || 0) - (fulfilled[data.material_id] || 0));
          if (remaining <= 0) {
            info = { type: "warn", msg: `${selectedMR.number}: requirement for ${data.material_code} already fulfilled (${mrItem.qty}/${mrItem.qty}).` };
            suggested = 0;
          } else if (remaining < data.qty_remaining) {
            suggested = remaining;
            info = { type: "ok", msg: `Auto-filled ${remaining} from ${selectedMR.number} remaining (box has ${data.qty_remaining}).` };
          } else if (remaining > data.qty_remaining) {
            suggested = data.qty_remaining;
            info = { type: "warn", msg: `Box has ${data.qty_remaining}, MR remaining ${remaining}. Issue this box, then scan next box for the balance ${remaining - data.qty_remaining}.` };
          }
        }
      }
      setIssueQty(suggested);
      setFeedback(info);
    } catch (e) {
      setFeedback({ type: "error", msg: formatApiErrorDetail(e.response?.data?.detail) || "Invalid barcode" });
      setLookup(null);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter") { e.preventDefault(); doLookup(input); }
  };

  const submitIssue = async () => {
    if (!lookup || !order) { setFeedback({ type: "error", msg: "Production order required" }); return; }
    try {
      const { data } = await api.post("/outward/issue", {
        barcode: input.trim(), issue_qty: +issueQty, production_order: order,
        material_request_id: mrId || null,
        ad_hoc_reason: !mrId ? adHocReason : null,
        fifo_override_reason: fifoOverride || null,
      });
      // FIFO override request created — pending PH/MD approval
      if (data.pending_fifo_request) {
        const req = data.request;
        setFeedback({ type: "warn", msg: `FIFO override request ${req.number} submitted to PH/MD for approval. Status: PENDING.` });
        setInput(""); setLookup(null); setIssueQty(""); setFifoOverride("");
        setTimeout(() => setFeedback(null), 4000);
        refocus();
        return;
      }
      const tag = data.material_request_number ? ` · ${data.material_request_number}` : (data.ad_hoc_reason ? " · AD-HOC" : "");
      const boxTag = data.box_no != null ? ` · BOX-${data.box_no}` : "";
      setFeedback({ type: "ok", msg: `ISSUED ${data.number}${tag}${boxTag}`, details: data });
      setInput(""); setLookup(null); setIssueQty(""); setFifoOverride("");
      setTimeout(() => setFeedback(null), 2500);
      refocus();
    } catch (e) {
      setFeedback({ type: "error", msg: formatApiErrorDetail(e.response?.data?.detail) || "Issue failed" });
    }
  };

  // MR running balance (Wave 10) — recalculate after every issue.
  // Wave-23 Ch-4: carry material_code/name through so the tile shows the
  // real material instead of the last-6 of the UUID.
  const mrBalance = useMemo(() => {
    if (!selectedMR) return null;
    const fulfilled = selectedMR.fulfilled_qty || {};
    return (selectedMR.items || []).map((it) => {
      const done = fulfilled[it.material_id] || 0;
      return {
        material_id: it.material_id,
        material_code: it.material_code || "",
        material_name: it.material_name || "",
        requested: it.qty,
        fulfilled: done,
        balance: Math.max(0, it.qty - done),
      };
    });
  }, [selectedMR, recent.length]);

  return (
    <div className="min-h-screen bg-[#0D2B5E] text-white" data-testid="outward-scan-page">
      <div className="flex items-center justify-between px-8 py-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-[#1565C0] flex items-center justify-center rounded-sm">
            <div className="w-4 h-4 bg-white" style={{ clipPath: "polygon(0 0, 100% 0, 50% 100%)" }} />
          </div>
          <div>
            <div className="text-lg font-bold tracking-widest">SUPPLAR<sup className="text-xs">®</sup></div>
            <div className="text-[10px] tracking-[0.3em] text-white/50 uppercase">Outward / Scan Terminal</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-xs text-white/60 uppercase tracking-widest">{user?.name} — {user?.role?.name}</div>
          <button onClick={() => nav("/")} className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/70 hover:text-white"><ArrowLeft size={14}/>Dashboard</button>
          <button onClick={async () => { await logout(); nav("/login"); }} className="text-xs uppercase tracking-widest text-white/70 hover:text-[#C62828]" data-testid="scan-logout-btn">Logout</button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-8 py-10">
        {/* STEP 1 — MR or ad-hoc reason */}
        <div className="mb-8 bg-white/5 border border-white/10 rounded-sm p-6">
          <div className="text-center mb-4">
            <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-[#2E7D32] mb-2">Step 1 — Authorisation</div>
            <div className="text-xl font-bold">Select an approved Material Request</div>
            <div className="text-xs text-white/50 mt-1">No outward without an approved MR{isPHorSA ? ", or PH/SA can use ad-hoc reason" : ""}</div>
            {step1Complete && (
              <button onClick={resetStep1} data-testid="scan-change-mr"
                className="mt-3 inline-flex items-center gap-1 text-[10px] tracking-widest uppercase font-bold text-[#FFB74D] hover:text-[#FFA000] underline-offset-2 hover:underline">
                ← Change MR / Reset Step 1
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] tracking-widest uppercase text-white/60 font-bold mb-1 flex items-center gap-2"><ListChecks size={12}/> Material Request</label>
              <select value={mrId} onChange={(e) => { setMrId(e.target.value); setAdHocReason(""); }} data-testid="scan-mr-step1"
                className="w-full text-base font-mono bg-[#0D2B5E] border-2 border-white/20 focus:border-[#2E7D32] outline-none py-2 px-3 text-white rounded-sm">
                <option value="">— Select an approved MR —</option>
                {mrs.map((m) => (
                  <option key={m.id} value={m.id}>{m.number} · {m.production_order || "no PO"} ({m.items.length} items, {m.status})</option>
                ))}
              </select>
              {selectedMR && (
                <div className="mt-2 text-[10px] tracking-widest uppercase text-[#2E7D32] font-bold">✓ {selectedMR.number} · {selectedMR.items.length} items · approved by {selectedMR.approved_by}</div>
              )}
            </div>
            <div>
              <label className="block text-[10px] tracking-widest uppercase text-white/60 font-bold mb-1 flex items-center gap-2"><AlertTriangle size={12}/> Ad-hoc Reason {isPHorSA ? "(PH/SA only)" : "(disabled)"}</label>
              <input value={adHocReason} disabled={!isPHorSA || !!mrId} onChange={(e) => setAdHocReason(e.target.value)} placeholder="e.g. R&D consumption / scrap" data-testid="scan-adhoc"
                className="w-full text-base font-mono bg-[#0D2B5E] border-2 border-white/20 focus:border-[#E65100] outline-none py-2 px-3 text-white rounded-sm disabled:opacity-30"/>
              <div className="text-[10px] uppercase tracking-widest text-white/40 mt-1">{!isPHorSA ? "Only PH/SA can do ad-hoc outward" : (mrId ? "Disabled — MR selected" : "")}</div>
            </div>
          </div>
          {mrBalance && mrBalance.length > 0 && (
            <div className="mt-4 flex items-center justify-between">
              <div className="text-[10px] tracking-widest uppercase text-white/60 font-bold">MR Balance · Auto-pick FIFO</div>
              <button onClick={fetchFIFOSuggest} disabled={!mrId || fifoLoading} data-testid="auto-fifo-btn"
                className={`inline-flex items-center gap-2 text-[11px] tracking-widest uppercase font-bold border-2 px-3 py-1.5 rounded-sm ${mrId && !fifoLoading ? "border-[#FFB74D] text-[#FFB74D] hover:bg-[#FFB74D] hover:text-[#0D2B5E]" : "border-white/20 text-white/30"}`}>
                <Wand2 size={14}/>{fifoLoading ? "Picking…" : (fifoSuggest ? "Re-pick FIFO" : "Auto-pick FIFO")}
              </button>
            </div>
          )}
          {mrBalance && mrBalance.length > 0 && (
            <div className="mt-4 grid grid-cols-3 gap-3" data-testid="mr-balance-bar">
              {mrBalance.map((b) => {
                const pct = b.requested ? Math.round((b.fulfilled / b.requested) * 100) : 0;
                const done = b.balance === 0;
                return (
                  <div key={b.material_id} className={`bg-[#0D2B5E] border rounded-sm p-3 ${done ? "border-[#2E7D32]" : "border-white/10"}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-mono font-bold text-[#90CAF9] truncate" title={b.material_code || b.material_id}>{b.material_code || b.material_id.slice(-6)}</div>
                        <div className="text-[11px] text-white/70 truncate mt-0.5" title={b.material_name}>{b.material_name || "—"}</div>
                      </div>
                      <div className={`text-2xl font-bold font-mono ${done ? "text-[#2E7D32]" : "text-[#FFB74D]"} leading-none`} data-testid={`mr-bal-${b.material_id}`}>{b.balance}</div>
                    </div>
                    <div className="text-[11px] text-white/60 font-mono mt-2 flex items-baseline justify-between">
                      <span><span className="text-lg font-bold text-white">{b.fulfilled}</span>/<span>{b.requested}</span> done</span>
                      <span>{pct}%</span>
                    </div>
                    <div className="h-1.5 bg-white/10 mt-1 rounded">
                      <div className="h-1.5 bg-[#2E7D32] rounded" style={{ width: `${Math.min(pct, 100)}%` }}/>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {fifoSuggest && (
            <div className="mt-4 bg-[#0D2B5E] border-2 border-[#FFB74D] rounded-sm p-3" data-testid="fifo-suggest-panel">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] tracking-widest uppercase text-[#FFB74D] font-bold flex items-center gap-1"><Wand2 size={12}/> Suggested FIFO Picks</div>
                <button onClick={() => setFifoSuggest(null)} className="text-[10px] tracking-widest uppercase text-white/60 hover:text-white" data-testid="fifo-suggest-close">Hide</button>
              </div>
              <div className="space-y-2">
                {(fifoSuggest.lines || []).map((line, li) => (
                  <div key={line.material_id || `line-${li}`} className="border border-white/10 rounded-sm p-2">
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-xs font-mono">
                        <span className="font-bold text-[#90CAF9]">{line.material_code || line.material_id.slice(-6)}</span>
                        <span className="text-white/70 ml-2">{line.material_name}</span>
                      </div>
                      <div className="text-[10px] uppercase tracking-widest text-white/70">
                        Need <span className="text-white font-bold font-mono">{line.required}</span>
                        {line.shortfall > 0 && <span className="text-[#C62828] ml-2 font-bold">Short by {line.shortfall}</span>}
                      </div>
                    </div>
                    {(line.boxes || []).length === 0 ? (
                      <div className="text-[10px] text-[#C62828] uppercase tracking-widest font-bold">No in-stock boxes available</div>
                    ) : (
                      <table className="w-full text-xs font-mono">
                        <thead className="text-[9px] uppercase tracking-widest text-white/40">
                          <tr>
                            <th className="text-left py-0.5">#</th>
                            <th className="text-left">GRN</th>
                            <th className="text-left">Lot</th>
                            <th className="text-right">Box</th>
                            <th className="text-right">Take</th>
                            <th className="text-right">In Stock Since</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {(line.boxes || []).map((b, bi) => {
                            const active = fifoCurrentIdx.line === li && fifoCurrentIdx.box === bi;
                            return (
                              <tr key={`${b.grn_number}-${b.lot_no}-${b.box_no ?? bi}`} className={active ? "bg-[#FFB74D]/20" : ""} data-testid={`fifo-pick-${li}-${bi}`}>
                                <td className="py-0.5 pr-1 text-white/50">{bi + 1}</td>
                                <td className="text-[#90CAF9]">{b.grn_number}</td>
                                <td className="text-white/80">{b.lot_no}</td>
                                <td className="text-right text-white">{b.box_no != null ? `#${b.box_no}` : "—"}</td>
                                <td className="text-right text-[#FFB74D] font-bold">{b.take}</td>
                                <td className="text-right text-[10px] text-white/40">{fmtIST(b.date_in)?.slice(0, 10) || "—"}</td>
                                <td className="text-right">
                                  <button onClick={() => applyFIFOPick(li, bi)} data-testid={`fifo-pick-apply-${li}-${bi}`}
                                    className="text-[9px] tracking-widest uppercase font-bold border border-[#2E7D32] text-[#2E7D32] hover:bg-[#2E7D32] hover:text-white px-1.5 py-0.5 rounded-sm">
                                    Pick
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                ))}
              </div>
              <div className="text-[10px] text-white/50 mt-2 leading-tight">
                Tip: click <strong className="text-[#2E7D32]">Pick</strong> on the next box to pre-fill the scan field — then press <strong className="text-white">Confirm Issue</strong>. You can edit the qty or skip to a newer lot (PH/MD approval will be requested automatically).
              </div>
            </div>
          )}
        </div>

        {/* STEP 2 — Scan */}
        <div className="mb-8">
          <div className="text-center mb-4">
            <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-[#2E7D32] mb-2">Step 2 — Scan</div>
            <div className="text-xl font-bold">Scan box QR code</div>
            <div className="text-sm text-white/50 mt-1">Per-box format: <span className="font-mono text-white/80">GRN|LOT|MAT|BOX-N</span></div>
          </div>
          <div className="flex items-center gap-3">
            <ScanLine className={step1Complete ? "text-[#2E7D32]" : "text-white/30"} size={32} strokeWidth={1.5}/>
            <input ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKey}
              disabled={!step1Complete}
              placeholder={step1Complete ? "GRN-…|LOT-…|MAT-…|BOX-1" : "Complete Step 1 first to enable scanning"} data-testid="scan-input-field"
              className={`flex-1 text-4xl font-mono text-white bg-transparent border-b-4 outline-none text-center py-4 tracking-widest ${step1Complete ? "border-[#1565C0] focus:border-[#2E7D32]" : "border-white/10 placeholder:text-white/30 cursor-not-allowed"}`}/>
          </div>
        </div>

        {feedback?.type === "error" && (
          <div className="bg-[#C62828] text-white p-6 rounded-sm mb-6 flex items-center gap-4" data-testid="scan-feedback-error">
            <XCircle size={36}/>
            <div><div className="text-[10px] tracking-[0.3em] uppercase opacity-80">Error</div><div className="text-xl font-bold">{feedback.msg}</div></div>
          </div>
        )}
        {feedback?.type === "ok" && (
          <div className="bg-[#2E7D32] text-white p-6 rounded-sm mb-6 flex items-center gap-4" data-testid="scan-feedback-ok">
            <CheckCircle2 size={36}/>
            <div><div className="text-[10px] tracking-[0.3em] uppercase opacity-80">Success</div><div className="text-xl font-bold">{feedback.msg}</div>{feedback.details && <div className="text-sm opacity-80 mt-1 font-mono">{feedback.details.material_name} — {feedback.details.issued_qty} units → {feedback.details.production_order}</div>}</div>
          </div>
        )}
        {feedback?.type === "warn" && (
          <div className="bg-[#E65100] text-white p-6 rounded-sm mb-6 flex items-center gap-4" data-testid="scan-feedback-warn">
            <AlertTriangle size={36}/>
            <div><div className="text-[10px] tracking-[0.3em] uppercase opacity-80">Notice</div><div className="text-xl font-bold">{feedback.msg}</div></div>
          </div>
        )}

        {/* STEP 3 — Confirm */}
        {lookup && (
          <div className="bg-white/5 border border-white/10 rounded-sm p-6 mb-6">
            <div className="grid grid-cols-5 gap-4 mb-5 text-sm">
              <DK k="GRN" v={lookup.grn_number}/>
              <DK k="Lot" v={lookup.lot_no}/>
              {lookup.box_no != null && <DK k="Box" v={`#${lookup.box_no}`} highlight/>}
              <DK k="Material" v={`${lookup.material_code}`}/>
              <DK k={lookup.box_no != null ? "Box Qty" : "Lot Qty"} v={lookup.qty_remaining} highlight/>
            </div>
            {lookup.fifo_warning && (
              <div className="bg-[#E65100]/30 border border-[#E65100] text-[#FFB74D] p-3 rounded-sm mb-4 flex items-start gap-2" data-testid="fifo-warning">
                <AlertTriangle size={20} className="mt-0.5"/>
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-widest font-bold mb-1">FIFO Violation</div>
                  <div className="text-xs">{lookup.fifo_warning}</div>
                  <div className="text-[10px] mt-1 text-white/70">{isPHorSA ? "You can override directly." : "Submitting will create a request for PH / MD approval."}</div>
                  <input value={fifoOverride} onChange={(e) => setFifoOverride(e.target.value)} placeholder={isPHorSA ? "FIFO override reason (PH/MD)" : "Reason for FIFO override request"} data-testid="fifo-override-reason"
                    autoFocus
                    onFocus={(e) => e.target.select()}
                    className="mt-2 w-full text-sm bg-[#0D2B5E] border border-[#E65100] outline-none py-1.5 px-2 text-white rounded-sm"/>
                </div>
              </div>
            )}
            <div className="text-center mb-4">
              <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-[#2E7D32] mb-2">Step 3 — Confirm</div>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-white/60 font-bold mb-1">Issue Qty</label>
                <input type="number" value={issueQty} onChange={(e) => setIssueQty(e.target.value)} data-testid="scan-issue-qty" className="w-full text-2xl font-mono bg-transparent border-b-2 border-white/20 focus:border-[#2E7D32] outline-none py-2 text-white"/>
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-white/60 font-bold mb-1">Production / Work Order</label>
                <input type="text" value={order} onChange={(e) => setOrder(e.target.value)} placeholder="WO-2026-0001" data-testid="scan-order" className="w-full text-xl font-mono bg-transparent border-b-2 border-white/20 focus:border-[#2E7D32] outline-none py-2 text-white"/>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => { setLookup(null); setInput(""); refocus(); }} className="px-5 py-3 border border-white/30 text-sm uppercase tracking-widest rounded-sm">Cancel</button>
              <button onClick={submitIssue} className="px-5 py-3 bg-[#2E7D32] hover:bg-[#1b5e20] text-white text-sm uppercase tracking-widest font-bold rounded-sm flex items-center gap-2" data-testid="confirm-issue-btn"><PackageCheck size={16}/> Confirm Issue</button>
            </div>
          </div>
        )}

        <div className="bg-white/5 border border-white/10 rounded-sm">
          <div className="px-5 py-3 border-b border-white/10 text-[10px] uppercase tracking-[0.2em] font-bold text-white/70">Recent Issues</div>
          <table className="w-full text-sm table-fixed">
            <colgroup>
              <col style={{ width: "12%" }}/><col style={{ width: "12%" }}/><col style={{ width: "16%" }}/><col style={{ width: "8%" }}/>
              <col style={{ width: "20%" }}/><col style={{ width: "8%" }}/><col style={{ width: "12%" }}/><col style={{ width: "12%" }}/>
            </colgroup>
            <thead className="text-[10px] uppercase tracking-widest text-white/50">
              <tr><th className="text-left px-3 py-2">Issue No.</th><th className="text-left px-3 py-2">GRN</th><th className="text-left px-3 py-2">Lot</th><th className="text-right px-3 py-2">Box</th><th className="text-left px-3 py-2">Material</th><th className="text-right px-3 py-2">Qty</th><th className="text-left px-3 py-2">Order/MR</th><th className="text-left px-3 py-2">At</th></tr>
            </thead>
            <tbody>
              {recent.map((r) => (
                <tr key={r.id} className="border-t border-white/5">
                  <td className="px-3 py-2 font-mono text-[#2E7D32] truncate">
                    <button onClick={() => setIssueDetail(r)} data-testid={`issue-no-${r.number}`}
                      className="hover:underline focus:underline focus:outline-none">{r.number}</button>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs truncate">{r.grn_number}</td>
                  <td className="px-3 py-2 font-mono text-xs truncate">{r.lot_no}</td>
                  <td className="px-3 py-2 font-mono text-xs text-right">{r.box_no || "—"}</td>
                  <td className="px-3 py-2 text-xs truncate">{r.material_code}</td>
                  <td className="px-3 py-2 font-mono text-right">{r.issued_qty}</td>
                  <td className="px-3 py-2 font-mono text-xs truncate" title={r.material_request_number || r.production_order}>{r.material_request_number || r.production_order}</td>
                  <td className="px-3 py-2 text-[10px] text-white/50 font-mono truncate">{fmtIST(r.issued_at)}</td>
                </tr>
              ))}
              {recent.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-sm text-white/50">No issues yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {issueDetail && <IssueDetail issue={issueDetail} onClose={() => setIssueDetail(null)}/>}
    </div>
  );
}

// Wave-G #5: Issue detail dialog launched from "Recent Issues" rows.
function IssueDetail({ issue, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-8" onClick={onClose} data-testid="issue-detail-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white border border-[#EEEEEE] rounded-sm shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b-4 border-[#2E7D32] flex items-start justify-between bg-[#F5F5F5]">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#2E7D32]">Issue Slip</div>
            <div className="text-2xl font-bold text-[#0D2B5E] font-mono">{issue.number}</div>
            <div className="text-xs text-[#0D2B5E]/60 font-mono mt-1">{fmtIST(issue.issued_at)} IST · {issue.issued_by_name || "—"}</div>
          </div>
          <button onClick={onClose} className="text-[#0D2B5E]/60 hover:text-[#C62828] p-1.5" data-testid="issue-detail-close">×</button>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-3 gap-4 text-sm">
            <DV k="Material" v={`${issue.material_code} — ${issue.material_name || ""}`}/>
            <DV k="GRN Ref." v={issue.grn_number}/>
            <DV k="Lot" v={issue.lot_no}/>
            <DV k="Box" v={issue.box_no != null ? `#${issue.box_no}` : "—"}/>
            <DV k="Qty Issued" v={issue.issued_qty} highlight/>
            <DV k="UOM" v={issue.uom || "—"}/>
            <DV k="Order / MR" v={issue.material_request_number || issue.production_order || "Ad-hoc"}/>
            <DV k="MR Reference" v={issue.material_request_id ? issue.material_request_id.slice(0, 8) + "…" : "—"}/>
            <DV k="Status" v={issue.deleted_at ? "DELETED" : "ACTIVE"} highlight/>
          </div>
          {issue.adhoc_reason && (
            <div className="border-l-4 border-[#E65100] pl-3 py-1">
              <div className="text-[10px] uppercase tracking-widest text-[#E65100] font-bold">Ad-hoc reason</div>
              <div className="text-sm text-[#0D2B5E]">{issue.adhoc_reason}</div>
            </div>
          )}
          {issue.fifo_override_id && (
            <div className="border-l-4 border-[#1565C0] pl-3 py-1">
              <div className="text-[10px] uppercase tracking-widest text-[#1565C0] font-bold">FIFO Override Applied</div>
              <div className="text-sm text-[#0D2B5E]/70">{issue.fifo_override_reason || "Approved by Plant Head"}</div>
            </div>
          )}
          {issue.deleted_at && (
            <div className="border-l-4 border-[#C62828] pl-3 py-1 bg-[#C62828]/5 rounded-sm">
              <div className="text-[10px] uppercase tracking-widest text-[#C62828] font-bold">Deleted</div>
              <div className="text-xs text-[#0D2B5E]/70 font-mono">{fmtIST(issue.deleted_at)} · {issue.deleted_by || "—"}</div>
              {issue.deletion_reason && <div className="text-sm mt-1">{issue.deletion_reason}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DV({ k, v, highlight }) {
  return (
    <div className="border-l-2 border-[#1565C0] pl-3">
      <div className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/50 font-bold">{k}</div>
      <div className={`text-base font-mono ${highlight ? "font-bold text-[#0D2B5E]" : "text-[#0D2B5E]/80"}`}>{v}</div>
    </div>
  );
}

function DK({ k, v, highlight }) {
  return (
    <div className={`border-l-2 pl-3 ${highlight ? "border-[#2E7D32]" : "border-[#1565C0]"}`}>
      <div className="text-[10px] tracking-widest uppercase text-white/50 font-bold">{k}</div>
      <div className={`text-lg font-medium font-mono ${highlight ? "text-[#2E7D32]" : "text-white"}`}>{v}</div>
    </div>
  );
}
